import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { RemunerationGroupRow, useGetComments } from '../hooks';

interface CommentModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (comment: string, onSuccess?: () => void) => void;
  row: RemunerationGroupRow | null;
  isLoading?: boolean;
}

export function CommentModal({
  open,
  onClose,
  onSave,
  row,
  isLoading = false,
}: CommentModalProps) {
  const [comment, setComment] = useState('');

  // Fetch existing comments when modal opens
  const {
    data: commentsData,
    isLoading: isLoadingComments,
    error: commentsError,
    refetch: refetchComments,
  } = useGetComments(row?.id || null);

  // Effect to refetch comments when modal opens or row changes
  useEffect(() => {
    console.log('Modal state changed:', { open, rowId: row?.id, row });
    if (open && row?.id) {
      console.log('Modal opened for row ID:', row.id);
      console.log('Triggering refetch for comments...');
      refetchComments();
    }
  }, [open, row?.id, refetchComments, row]);

  const handleSave = () => {
    if (comment.trim()) {
      onSave(comment.trim(), () => {
        // Clear the comment input after successful save
        setComment('');
        // Refetch comments to show the new comment
        refetchComments();
      });
    }
  };

  const handleClose = () => {
    setComment('');
    onClose();
  };

  const handleCancel = () => {
    setComment('');
    onClose();
  };

  // Format date for display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          pb: 2,
          pt: 2,
          borderBottom: '1px solid #E1E8ED',
          minHeight: '80px',
        }}
      >
        <Box sx={{ flex: 1, pr: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#063142', mb: 1 }}>
            Add Comment
          </Typography>
          {commentsData ? (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" sx={{ color: '#666', mb: 0.5 }}>
                <strong>Name of Group:</strong> {commentsData.rgName}
              </Typography>
              <Typography variant="body2" sx={{ color: '#666', mb: 0.5 }}>
                <strong>Sending DO:</strong> {commentsData.sendingPpo}
              </Typography>
              <Typography variant="body2" sx={{ color: '#666', mb: 0.5 }}>
                <strong>Receiving DO:</strong> {commentsData.receivingPpo}
              </Typography>
              <Typography variant="body2" sx={{ color: '#666', mb: 0.5 }}>
                <strong>Year:</strong> {commentsData.year}
              </Typography>
              <Typography variant="body2" sx={{ color: '#666' }}>
                <strong>Quarter:</strong> {commentsData.quarter}
              </Typography>
            </Box>
          ) : row && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" sx={{ color: '#666', mb: 0.5 }}>
                <strong>Name of Group:</strong> {row.remunerationGroup?.displayName || row.remunerationGroup?.name}
              </Typography>
              <Typography variant="body2" sx={{ color: '#666', mb: 0.5 }}>
                <strong>Sending DO:</strong> {row.sendingPpo?.name}
              </Typography>
              <Typography variant="body2" sx={{ color: '#666', mb: 0.5 }}>
                <strong>Receiving DO:</strong> {row.receivingPpo?.name}
              </Typography>
              <Typography variant="body2" sx={{ color: '#666', mb: 0.5 }}>
                <strong>Year:</strong> {row.year}
              </Typography>
              <Typography variant="body2" sx={{ color: '#666' }}>
                <strong>Quarter:</strong> {row.quarter}
              </Typography>
            </Box>
          )}
        </Box>
        <IconButton
          onClick={handleClose}
          sx={{
            color: '#666',
            flexShrink: 0,
            '&:hover': {
              backgroundColor: '#f5f5f5',
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3, pb: 2, px: 3 }}>
        {/* Existing Comments Section */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#063142' }}>
            Existing Comments ({commentsData?.remunerationGroupCommentDtos?.length || 0})
          </Typography>
          
          {commentsError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              Failed to load comments. Please try again.
            </Alert>
          )}
          
          {isLoadingComments ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <TableContainer 
              component={Paper} 
              sx={{ 
                maxHeight: 300, 
                border: '1px solid #E1E8ED',
                borderRadius: '8px',
                mb: 2,
              }}
            >
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, backgroundColor: '#f8fafc' }}>
                      Comment
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, backgroundColor: '#f8fafc', width: '150px' }}>
                      Date
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, backgroundColor: '#f8fafc', width: '120px' }}>
                      DO
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {commentsData?.remunerationGroupCommentDtos?.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} sx={{ textAlign: 'center', py: 3, color: '#666' }}>
                        No comments found
                      </TableCell>
                    </TableRow>
                  ) : (
                    commentsData?.remunerationGroupCommentDtos?.map((comment, index) => (
                      <TableRow key={index}>
                        <TableCell sx={{ wordBreak: 'break-word' }}>
                          {comment.comment}
                        </TableCell>
                        <TableCell sx={{ color: '#666', fontSize: '0.875rem' }}>
                          {formatDate(comment.creationDate)}
                        </TableCell>
                        <TableCell sx={{ color: '#666', fontSize: '0.875rem' }}>
                          {comment.creationPpo}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Add New Comment Section */}
        <Box>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#063142' }}>
            Add New Comment
          </Typography>
          <TextField
            autoFocus
            multiline
            rows={4}
            fullWidth
            label="Comment"
            placeholder="Enter your comment here..."
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            variant="outlined"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#00ACE7',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#00ACE7',
                },
              },
              '& .MuiInputLabel-root': {
                '&.Mui-focused': {
                  color: '#00ACE7',
                },
              },
            }}
          />
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          pb: 3,
          pt: 2,
          gap: 1,
          borderTop: '1px solid #E1E8ED',
          mt: 1,
        }}
      >
        <Button
          onClick={handleCancel}
          variant="outlined"
          disabled={isLoading}
          sx={{
            borderColor: '#E1E8ED',
            color: '#666',
            '&:hover': {
              borderColor: '#ccc',
              backgroundColor: '#f5f5f5',
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={!comment.trim() || isLoading}
          sx={{
            backgroundColor: '#00ACE7',
            color: '#FFFFFF',
            '&:hover': {
              backgroundColor: '#0095C7',
            },
            '&:disabled': {
              backgroundColor: '#ccc',
            },
          }}
        >
          {isLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={16} color="inherit" />
              Saving...
            </Box>
          ) : (
            'Save Comment'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
